import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MoedaPipe } from '@nomoh-monorepo/data-access-shared-i18n';

import {
  AgendaDataService,
  AgendaService,
  EstabelecimentoDataService,
  NichoTermosService,
} from '@nomoh-monorepo/data-access-bardo-administrador';

import { ConnectionService } from '@nomoh-monorepo/data-access-shared-connection';
import { GeolocationService } from '@nomoh-monorepo/shared-angular-util-tools';
import { DeviceService, TabsInkbarFixDirective } from '@nomoh-monorepo/shared-common-ui';
import {
  IAgendamento,
  ICapturaLocalizacao,
  StatusAgendamentoEnum,
} from '@nomoh-monorepo/shared-types';
import { firstValueFrom } from 'rxjs';
import { formatarTelefone } from '@nomoh-monorepo/shared-util-tools';
import { DateTime } from 'luxon';
import { TabsModule } from 'primeng/tabs';
import { AgendaNovoAgendamentoComponent } from '../../agenda-modal-novo-agendamento/agenda-novo-agendamento/agenda-novo-agendamento.component';
/**
 * Modal de informações detalhadas de um agendamento
 * Exibe: Status, Cliente, Telefone, Serviço, Profissional, Observação
 * Ações: Cancelar, Confirmar, Finalizar (baseado no status)
 */
@Component({
  selector: 'lib-agenda-informacao-agendamento',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    FormsModule,
    TextareaModule,
    TabsModule,
    TabsInkbarFixDirective,
    AgendaNovoAgendamentoComponent,
    TranslateModule,
    MoedaPipe,
  ],
  templateUrl: './agenda-informacao-agendamento.component.html',
  styleUrl: './agenda-informacao-agendamento.component.scss',
})
export class AgendaInformacaoAgendamentoComponent
  implements AfterViewInit, OnChanges
{
  private readonly timeZonePadrao = 'America/Sao_Paulo';

  //#region Injeção de Dependências
  readonly AgendaService = inject(AgendaService);
  readonly AgendaDataService = inject(AgendaDataService);
  private readonly estabelecimentoDataService = inject(EstabelecimentoDataService);
  readonly connectionService = inject(ConnectionService);
  private readonly translateService = inject(TranslateService);
  private readonly geolocationService = inject(GeolocationService);
  private readonly deviceService = inject(DeviceService);
  $nicho = inject(NichoTermosService).$nicho;

  //#endregion

  //#region enums
  readonly StatusAgendamento = StatusAgendamentoEnum;
  //#endregion

  //#region signals
  readonly cancelarDialogVisible = signal(false);
  readonly observacaoCancelamento = signal('');
  activeTab = signal<string>('view');
  //#endregion

  //#region inputs
  @Input() showCloseButton = true;
  //#endregion

  //#region outputs
  @Output() fechar = new EventEmitter<void>();
  //#endregion

  //#region propriedades
  readonly maxObservacaoLength = 255;
  readonly tabs = computed(() => [
    {
      id: 'view',
      label:
        (this.translateService.instant('BARDO_AGENDA.DADOS') as string) ||
        'Dados',
      icon: 'pi pi-eye',
    },
    {
      id: 'reagendar',
      label:
        (this.translateService.instant('BARDO_AGENDA.REAGENDAR') as string) ||
        'Reagendar',
      icon: 'pi pi-calendar',
    },
    {
      id: 'cancelar',
      label:
        (this.translateService.instant('BARDO_AGENDA.CANCELAR') as string) ||
        'Cancelar',
      icon: 'pi pi-times',
    },
  ]);
  //#endregion

  //#region ViewChilds
  @ViewChild('headerTemplate', { static: false })
  private _headerTemplateRef!: TemplateRef<unknown>;

  @ViewChild('footerTemplate', { static: false })
  private _footerTemplateRef!: TemplateRef<unknown>;
  //#endregion

  //#region inputs/outputs
  @Input() agendamento: IAgendamento = {} as IAgendamento;
  @Output() headerTemplateAction = new EventEmitter<TemplateRef<unknown>>();
  @Output() footerTemplateAction = new EventEmitter<TemplateRef<unknown>>();
  //#endregion

  constructor() {
    effect(() => {
      const agendamento = this.AgendaDataService.$agendamentos().find(
        (f) => f.id == this.agendamento.id
      );

      if (agendamento) {
        this.agendamento = agendamento;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['agendamento']) {
      this.activeTab.set('view');
      this.observacaoCancelamento.set('');
    }
  }
  ngAfterViewInit(): void {
    this.headerTemplateAction.emit(this._headerTemplateRef);
    this.footerTemplateAction.emit(this._footerTemplateRef);
  }

  onChildHeaderTemplate(template: TemplateRef<unknown>): void {
    this.headerTemplateAction.emit(template);
  }

  onChildFooterTemplate(template: TemplateRef<unknown>): void {
    this.footerTemplateAction.emit(template);
  }

  //#region lógica de tabs
  tabVisivel(tabId: string): boolean {
    switch (tabId) {
      case 'view':
        return true;
      case 'cancelar':
      case 'reagendar':
        return (
          this.agendamento?.status === this.StatusAgendamento.CONFIRMADO ||
          this.agendamento?.status === this.StatusAgendamento.PENDENTE
        );
      default:
        return false;
    }
  }

  onTabChange(tabId: string) {
    if (this.activeTab() === 'reagendar' && tabId !== 'reagendar') {
      this.headerTemplateAction.emit(this._headerTemplateRef);
      this.footerTemplateAction.emit(this._footerTemplateRef);
    }
    this.activeTab.set(tabId);
  }

  //#endregion

  //#region tab cancelar

  async confirmarCancelamento(): Promise<void> {
    const localizacao = await this.capturarLocalizacaoDomicilio();

    const sucesso = await this.AgendaService.cancelarAgendamentoModalInformacao(
      this.agendamento,
      this.observacaoCancelamento(),
      localizacao
    );

    if (sucesso) {
      this.fechar.emit();
    }
  }

  async confirmarAgendamento(): Promise<void> {
    if (
      !this.agendamento ||
      this.AgendaDataService.$isLoadingModalInformacao()
    ) {
      return;
    }

    const localizacao = await this.capturarLocalizacaoDomicilio();

    const sucesso = await this.AgendaService.aprovarAgendamentoModalInformacao(
      this.agendamento,
      localizacao
    );

    if (sucesso) {
      this.fechar.emit();
    }
  }

  //#endregion

  //#region tab visualizar

  async finalizarAtendimento(): Promise<void> {
    if (!this.agendamento) {
      return;
    }

    const localizacao = await this.capturarLocalizacaoDomicilio();

    const sucesso = await this.AgendaService.realizarAgendamentoModalInformacao(
      this.agendamento,
      localizacao
    );

    if (sucesso) {
      this.fechar.emit();
    }
  }
  //#endregion

  //#region tab reagendar

  abrirReagendamento(): void {
    if (!this.agendamento) {
      return;
    }
  }
  //#endregion

  //#region utilitários
  rotuloStatus(status: number): string {
    switch (status) {
      case StatusAgendamentoEnum.CANCELADO:
        return this.translateService.instant('BARDO_STATUS.CANCELADO');
      case StatusAgendamentoEnum.CONFIRMADO:
        return this.translateService.instant('BARDO_STATUS.CONFIRMADO');
      case StatusAgendamentoEnum.REALIZADO:
        return this.translateService.instant('BARDO_STATUS.REALIZADO');
      default:
        return this.translateService.instant('BARDO_STATUS.PENDENTE');
    }
  }

  rotuloDispositivo(dispositivo: string): string {
    switch (dispositivo) {
      case 'mobile':
        return this.translateService.instant('BARDO_AGENDA.DISPOSITIVO_MOBILE');
      case 'tablet':
        return this.translateService.instant('BARDO_AGENDA.DISPOSITIVO_TABLET');
      default:
        return this.translateService.instant('BARDO_AGENDA.DISPOSITIVO_DESKTOP');
    }
  }

  private async capturarLocalizacaoDomicilio(): Promise<
    ICapturaLocalizacao | undefined
  > {
    if (!this.agendamento?.isDomicilio) {
      return undefined;
    }

    try {
      const posicao = await firstValueFrom(
        this.geolocationService.getCurrentPosition()
      );
      if (!posicao) {
        return this.localizacaoSemGps();
      }

      return {
        latitude: posicao.latitude,
        longitude: posicao.longitude,
        dispositivo: this.deviceService.getDeviceType(),
      };
    } catch {
      return this.localizacaoSemGps();
    }
  }

  private localizacaoSemGps(): ICapturaLocalizacao {
    return {
      dispositivo: this.deviceService.getDeviceType(),
      geolocalizacao: this.translateService.instant(
        'GEOLOCALIZACAO.ERRO_HISTORICO'
      ),
    };
  }

  formatarTelefone(telefone?: string | null): string {
    if (!telefone) {
      return 'Não informado';
    }
    return formatarTelefone(telefone);
  }

  formatarDataHoraAgendamento(dataHora: Date | string | null | undefined): string {
    if (!dataHora) {
      return '';
    }

    const dateTime =
      dataHora instanceof Date
        ? DateTime.fromJSDate(dataHora)
        : DateTime.fromISO(dataHora);

    return dateTime.isValid
      ? dateTime
          .setZone(this.getTimeZoneEstabelecimento())
          .toFormat('dd/MM - HH:mm')
      : '';
  }

  private getTimeZoneEstabelecimento(): string {
    const timeZone =
      this.estabelecimentoDataService
        .$estabelecimentoAtual()
        ?.timeZone?.trim() || this.timeZonePadrao;
    return DateTime.now().setZone(timeZone).isValid
      ? timeZone
      : this.timeZonePadrao;
  }
  //#endregion
}
